import React, { useEffect, useMemo, useState } from 'react'

import {
  EditBillingDetailsContent,
  EditBillingDetailsModalStyled,
  EditBillingDetailsRow,
  ErrorStyled,
  vatInfoBorderStyles,
  vatInfoTriggerStyles,
  animatedInputStyles,
  InputWrapper,
  EditBillingDetailsSubTitle,
  EditBillingDetailsHelpSection,
} from 'Features/accountSettings/components/Billing/EditBillingDetailsModal/EditBillingDetailsModal.styled'
import {
  Modal,
  Button,
  ModalControlsStyled,
  InputWithTitle,
  GeneralSelect,
  AsyncSelectCq,
  Tooltip,
  useFrontEndEventTrackingService,
} from '@clickcease/cc-client-components-lib'
import { useGetAccountSettings } from 'Features/accountSettings/api/getBilling'
import { useUpdateAccountSettings } from 'Features/accountSettings/api/updateAccountSettings'
import { Trans, useTranslation } from 'react-i18next'
import { Countries } from 'Assets/lists/countries'
import UsStates from 'Assets/lists/usStates'
import { canadianProvinces, ProvincialTaxIdProvinces } from 'Assets/lists/caProvinces'
import { SelectOption } from 'Types/SelectOption'
import { validateEmail } from 'Utils/validation'
import { AddressComponent } from 'Features/accountSettings/components/Billing/EditBillingDetailsModal/types'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { ServerError } from 'Lib/axios'
import { useGoogleAddressSearch } from 'Hooks/useGoogleAddressSearch'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'
import { Link } from 'react-router-dom'
import { useZendeskChat } from 'Hooks/zendesk/useZendeskChat'
import { CommonlyUsedCountries } from 'Utils/commonlyUsedCountries'

interface EditBillingDetailsModalProps {
  isOpen?: boolean
  onOpenChange: (a: boolean) => void
  source?: string
}

type EditBillingDetailsForm = {
  accountName: string
  companyName: string
  companyVatId: string
  provinceCompanyId?: string
  billingEmail?: string
  state?: string
  street: string
  zipCode: string
  country: string
  city: string
  isBusiness?: boolean
}

export const EditBillingDetails = ({
  isOpen,
  onOpenChange,
  source,
}: EditBillingDetailsModalProps) => {
  const { t } = useTranslation('accountSettings')
  const { t: tCheckout } = useTranslation('checkout')
  const { showToaster } = useAppStore()
  const [accountSettings] = useGetAccountSettings()
  const { openChat } = useZendeskChat()
  const { data: planDetails } = useGetPlansDetails()
  const { track } = useFrontEndEventTrackingService()
  const { accountId, userId, email } = useUserStore()
  const { loadOptions, getPlaceDetails, isLoadingSearch, setCountry } = useGoogleAddressSearch({
    onAddressSelect: (address) => {
      setForm((prev) => ({
        ...prev,
        street: address.street,
        zipCode: address.postalCode,
        city: address.city,
        state: address.state || '',
      }))
    },
  })

  const accounts = useMemo(() => {
    if (!(accountSettings.data instanceof Error)) {
      return accountSettings.data
    }
  }, [accountSettings])

  const { mutate, isLoading: isLoadingUpdate } = useUpdateAccountSettings()

  const popUpModalSource = source === 'home'

  const initForm = {
    accountName: '',
    companyName: '',
    companyVatId: '',
    provinceCompanyId: '',
    street: '',
    zipCode: '',
    country: '',
    city: '',
  }
  const [form, setForm] = useState<EditBillingDetailsForm>(initForm)
  const [isMounted, setIsMounted] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [invalidFields, setInvalidFields] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (accounts && !isMounted) {
      const fillForm = {
        accountName: accounts.accountName || '',
        country: accounts.country,
        billingEmail: accounts.billingEmail,
        companyName: accounts.companyName,
        companyVatId: accounts.companyVatId,
        provinceCompanyId: accounts.provinceCompanyId,
        street: accounts.street,
        zipCode: accounts.zipCode,
        city: accounts.city,
        state: accounts.state || '',
      }
      if (
        (accounts.country === CommonlyUsedCountries.US ||
          accounts.country === CommonlyUsedCountries.CA) &&
        accounts.state
      ) {
        fillForm['state'] = accounts.state
      }

      setCountry(accounts.country)
      setForm(fillForm)
      setIsMounted(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, isMounted])

  useEffect(() => {
    if (source === 'home') {
      track(['segment'], 'billing_popup_viewed', {
        account_id: accountId,
        user_id: userId,
        email,
        source: 'post-login',
        country: accounts?.country,
        subscription_id: planDetails?.billingSubscriptionId,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (
      form.country &&
      form.country !== CommonlyUsedCountries.US &&
      form.country !== CommonlyUsedCountries.CA
    ) {
      setForm((prevForm) => ({
        ...prevForm,
        state: '',
      }))
    }
    if (form.country && form.country !== CommonlyUsedCountries.CA) {
      setForm((prevForm) => ({
        ...prevForm,
        provinceCompanyId: '',
      }))
    }
  }, [form.country])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    })
  }

  const setSelectedField = (type: string, value: string) => {
    setForm({
      ...form,
      [type]: value,
    })
  }

  const closeModal = () => {
    setForm(initForm)
    setIsMounted(false)
    onOpenChange(false)
  }

  const fieldCss = {
    width: '218px',
    height: '33px',
  }

  const validateForm = () => {
    const requiredFields = {
      street: form.street,
      zipCode: form.zipCode,
      country: form.country,
      city: form.city,
    }

    const missingFields = Object.entries(requiredFields).reduce((acc, [key, value]) => {
      if (value.length === 0) {
        acc[key] = true
      }
      return acc
    }, {} as Record<string, boolean>)

    const hasErrors = Object.keys(missingFields).length > 0

    if (hasErrors) {
      setInvalidFields(missingFields)
      setError(t('edit_billing_details_modal.all_fields_are_mandatory'))

      setTimeout(() => {
        setInvalidFields({})
      }, 1500)

      return false
    }

    if (form.country?.toUpperCase() === CommonlyUsedCountries.US && !form.state) {
      setError(t('edit_billing_details_modal.please_select_a_state'))
      setInvalidFields({ state: true })
      setTimeout(() => {
        setInvalidFields({})
      }, 1500)
      return false
    }

    if (form.billingEmail && !validateEmail(form.billingEmail)) {
      setError(t('billing_page.email_is_not_valid'))
      setInvalidFields({ billingEmail: true })
      setTimeout(() => {
        setInvalidFields({})
      }, 1500)
      return false
    }

    if (form.companyName && form.country !== CommonlyUsedCountries.US && !form.companyVatId) {
      setError(t('billing_page.please_fill_company_id'))
      setInvalidFields({ companyVatId: true })
      setTimeout(() => {
        setInvalidFields({})
      }, 1500)
      return false
    }

    if (
      (form.companyVatId && !form.companyName) ||
      (isTaxRequiredCanadianProvince && !form.companyName && form.provinceCompanyId)
    ) {
      setError(t('billing_page.please_fill_company_name'))
      setInvalidFields({ companyName: true })
      setTimeout(() => {
        setInvalidFields({})
      }, 1500)
      return false
    }

    if (form.billingEmail && !validateEmail(form.billingEmail)) {
      setError(t('billing_page.email_is_not_valid'))
      setInvalidFields({ billingEmail: true })
      setTimeout(() => {
        setInvalidFields({})
      }, 1500)
      return false
    }

    if (form.accountName.length > 30) {
      setError(t('billing_page.account_name_length_invalid'))
      setInvalidFields({ accountName: true })
      setTimeout(() => {
        setInvalidFields({})
      }, 1500)
      return false
    }

    setError('')
    setInvalidFields({})
    return true
  }

  const updateData = () => {
    const updatedForm = { ...form }
    if (validateForm()) {
      if (
        updatedForm.country !== CommonlyUsedCountries.US &&
        updatedForm.country !== CommonlyUsedCountries.CA
      ) {
        updatedForm.state = ''
      }
      updatedForm.isBusiness = !!updatedForm.companyVatId

      mutate(updatedForm, {
        onSuccess: (response) => {
          if (response?.httpStatusCode === 200) {
            if (popUpModalSource) {
              track(['segment'], 'billing_details_update_complete', {
                account_id: accountId,
                user_id: userId,
                email,
                submitted_from: 'popup',
                country: form.country,
                vat_id: form.companyVatId,
                province_company_id: form.provinceCompanyId,
              })
            }
            closeModal()
            showToaster(
              t('edit_billing_details_modal.billing_details_updated_successfully'),
              'success'
            )
          } else {
            if (response?.httpStatusCode === 400) {
              const vatErrorMessage = (response as ServerError).message
              setError(tCheckout(vatErrorMessage))
            }
          }
        },
      })
    }
  }

  const countryVat = tCheckout(`vatCountriesNames.${form.country}`)
  const isTaxRequiredCanadianProvince =
    ProvincialTaxIdProvinces.some(
      (province) => province.value === form.state || province.label === form.state
    ) && form.country === CommonlyUsedCountries.CA
  return (
    <Modal
      title={
        popUpModalSource
          ? t('edit_billing_details_modal.pop_up_title')
          : t('edit_billing_details_modal.title')
      }
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      id={'edit-billing-details-modal'}
    >
      <EditBillingDetailsModalStyled>
        {popUpModalSource && (
          <EditBillingDetailsSubTitle>
            {t('edit_billing_details_modal.sub_title')}
          </EditBillingDetailsSubTitle>
        )}
        <EditBillingDetailsContent>
          <EditBillingDetailsRow>
            <InputWithTitle
              name="accountName"
              css={{
                ...fieldCss,
                ...animatedInputStyles(invalidFields.accountName),
              }}
              title={t('edit_billing_details_modal.account_name')}
              value={form.accountName}
              onChange={handleInputChange}
            />
          </EditBillingDetailsRow>
          <EditBillingDetailsRow css={{ div: { width: '100%' } }}>
            <InputWithTitle
              name="billingEmail"
              css={{ height: '33px', ...animatedInputStyles(invalidFields.billingEmail) }}
              title={t('edit_billing_details_modal.billing_email')}
              value={form.billingEmail}
              onChange={handleInputChange}
            />
          </EditBillingDetailsRow>
          <EditBillingDetailsRow>
            <InputWithTitle
              name="companyName"
              css={{
                width: isTaxRequiredCanadianProvince ? '220px' : '330px',
                height: '33px',
                ...animatedInputStyles(invalidFields.companyName),
              }}
              title={t('edit_billing_details_modal.company_name')}
              value={form.companyName}
              onChange={handleInputChange}
            />
            <InputWrapper>
              <InputWithTitle
                name="companyVatId"
                css={{
                  width: isTaxRequiredCanadianProvince ? '220px' : '330px',
                  height: '33px',
                  ...animatedInputStyles(invalidFields.companyVatId),
                }}
                title={
                  countryVat === `vatCountriesNames.${form.country}`
                    ? tCheckout('VatID')
                    : countryVat
                }
                value={form.companyVatId}
                onChange={handleInputChange}
              />
              <Tooltip
                tooltiptContent={tCheckout('vat_explainer')}
                constentStyle={vatInfoBorderStyles}
                trigerStyle={{ style: vatInfoTriggerStyles }}
              />
            </InputWrapper>
            {isTaxRequiredCanadianProvince && (
              <InputWrapper>
                <InputWithTitle
                  name="provinceCompanyId"
                  css={{
                    width: isTaxRequiredCanadianProvince ? '220px' : '330px',
                    height: '33px',
                    ...animatedInputStyles(invalidFields.provinceCompanyId),
                  }}
                  title={tCheckout('provincialTaxId')}
                  value={form.provinceCompanyId}
                  onChange={handleInputChange}
                />
                <Tooltip
                  tooltiptContent={tCheckout('provincialTaxId_explainer')}
                  constentStyle={vatInfoBorderStyles}
                  trigerStyle={{ style: vatInfoTriggerStyles }}
                />
              </InputWrapper>
            )}
          </EditBillingDetailsRow>
          <EditBillingDetailsRow>
            <GeneralSelect
              title={t('edit_billing_details_modal.country')}
              value={Countries.find(
                (item) => item.label === form.country || item.value === form.country
              )}
              wrapperCSS={{
                ...fieldCss,
                '> div:nth-child(2)': { marginTop: '5px' },
                '> div > div[class$="-control"]': { height: '33px' },
              }}
              selectCss={{ ...animatedInputStyles(invalidFields.country) }}
              options={Countries}
              topTitle
              onChange={(item: SelectOption) => {
                if (item) {
                  setSelectedField('country', item?.value)
                  setCountry(item?.value)
                }
              }}
            />
            {(form.country === CommonlyUsedCountries.US ||
              form.country === CommonlyUsedCountries.CA) && (
              <GeneralSelect
                key={`state-select-${form.state || 'empty'}`}
                title={
                  form.country === CommonlyUsedCountries.US
                    ? t('edit_billing_details_modal.state')
                    : t('edit_billing_details_modal.province')
                }
                value={(form.country === CommonlyUsedCountries.US
                  ? UsStates
                  : canadianProvinces
                ).find((item) => item.value === form.state || item.label === form.state)}
                wrapperCSS={{
                  ...fieldCss,
                  '> div:nth-child(2)': { marginTop: '5px' },
                  '> div > div[class$="-control"]': { height: '33px' },
                }}
                selectCss={{ ...animatedInputStyles(invalidFields.state) }}
                options={form.country === CommonlyUsedCountries.US ? UsStates : canadianProvinces}
                topTitle
                onChange={(item: SelectOption) => {
                  if (item) setSelectedField('state', item?.value)
                }}
              />
            )}
            <InputWithTitle
              disabled={form.country.length === 0}
              name="city"
              css={{ ...fieldCss, ...animatedInputStyles(invalidFields.city) }}
              title={t('edit_billing_details_modal.city')}
              value={form.city}
              onChange={handleInputChange}
            />
            <InputWithTitle
              name="zipCode"
              css={{ ...fieldCss, ...animatedInputStyles(invalidFields.zipCode) }}
              title={t('edit_billing_details_modal.zip_code')}
              value={form.zipCode}
              onChange={handleInputChange}
            />
            <AsyncSelectCq
              wrapperCSS={{
                width:
                  form.country === CommonlyUsedCountries.US ||
                  form.country === CommonlyUsedCountries.CA
                    ? '454px'
                    : '100%',
                position: 'relative',
                height: '40px',
                ...animatedInputStyles(invalidFields.street),
              }}
              defaultValue={{
                label: form.street,
                value: '0',
                addressComponents: [] as AddressComponent[],
              }}
              onChange={getPlaceDetails}
              loadOptions={loadOptions}
              title={t('edit_billing_details_modal.address')}
              placeholder={'Search for address'}
              isLoading={isLoadingSearch}
              css={{ height: '34px' }}
            />
          </EditBillingDetailsRow>
          {popUpModalSource && (
            <EditBillingDetailsHelpSection>
              <Trans
                ns="accountSettings"
                i18nKey="edit_billing_details_modal.support"
                components={{ support: <Link to="#" onClick={openChat} /> }}
              />
            </EditBillingDetailsHelpSection>
          )}
        </EditBillingDetailsContent>
        <ModalControlsStyled>
          <Button
            variant="ghost"
            text={t('edit_billing_details_modal.cancel')}
            onClick={() => {
              closeModal()
            }}
          />
          <Button
            text={t('edit_billing_details_modal.update')}
            loading={isLoadingUpdate}
            onClick={() => updateData()}
          />
        </ModalControlsStyled>
        <ErrorStyled fixMargin={form.country !== CommonlyUsedCountries.US}>{error}</ErrorStyled>
      </EditBillingDetailsModalStyled>
    </Modal>
  )
}
